"use client";

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card_v3";
import { cn } from "lib/utils";
import localization from "services/localization";
import { format } from "date-fns";

export default function PolicyRiskChart({ messages = [], theme, departments = [],users = [],selectedUser,
  selectedRole,
  selectedDepartment }) {
  const card_bg =
    theme === "dark" ? "bg-[var(--card-bg)] border-border" : "bg-card border-border";

    const departmentsMap = useMemo(() => {
      const map = {};
      departments.forEach((d) => (map[d.id] = d));
      return map;
    }, [departments]);

    const usersMap = useMemo(() => {
  const map = {};
  users.forEach((u) => (map[u.id] = u));
  return map;
}, [users]);

  // Prepare data by department
const data = useMemo(() => {
  if (!messages?.length) return [];

  const buildStats = (keyGetter, labelGetter) => {
    const map = {};

    messages.forEach((item) => {
      const key = keyGetter(item);
      if (!key) return;

      const totalMsgs = item.metrics?.total_messages || 0;
      const risk = item.metrics?.avg_policy_risk || 0;

      if (!map[key]) {
        map[key] = { totalRisk: 0, totalMessages: 0 };
      }

      map[key].totalRisk += risk * totalMsgs;
      map[key].totalMessages += totalMsgs;
    });

    const MAX_LABEL_LENGTH = 15;
    return Object.entries(map).map(([key, stats]) => {
      const avg =
        stats.totalMessages > 0
          ? stats.totalRisk / stats.totalMessages
          : 0;
      const fullName = labelGetter(key);
  const name = fullName.length > MAX_LABEL_LENGTH
    ? fullName.slice(0, MAX_LABEL_LENGTH) + "..." // truncate for X-axis
    : fullName;

      return {
        name,       // truncated for X-axis
    fullName,   // full name for tooltip
        risk: parseFloat(avg.toFixed(1)),
        riskPercent: parseFloat(((parseFloat(avg.toFixed(1)) / 5) * 100).toFixed(1)),
      };
    });
  };

  // ✅ 1. USER → show over time
  if (selectedUser && selectedUser !== "all") {
    const map = {};

    messages.forEach((m) => {
      const date = m.date;
      const totalMsgs = m.metrics?.total_messages || 0;
      const risk = m.metrics?.avg_policy_risk || 0;

      if (!map[date]) {
        map[date] = { totalRisk: 0, totalMessages: 0 };
      }

      map[date].totalRisk += risk * totalMsgs;
      map[date].totalMessages += totalMsgs;
    });

    return Object.entries(map)
     .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([date, stats]) => {
      const avg =
        stats.totalMessages > 0
          ? stats.totalRisk / stats.totalMessages
          : 0;

      return {
        name: format(new Date(date), "MMM dd"),
        fullName: format(new Date(date), "PPP"),
        risk: parseFloat(avg.toFixed(1)),
        riskPercent: parseFloat(((avg / 5) * 100).toFixed(1)),
      };
    });
  }

 // ✅ 2. ROLE → group by departments of users with that role
if (selectedRole && selectedRole !== "all") {
   // Get all user IDs with this role
  const roleUserIds = new Set(users.filter(u => u.role === selectedRole).map(u => u.id));

  return buildStats(
    // group by department_id in message
    (item) => {
      if (!roleUserIds.has(item.user_id)) return null; // ignore messages not from role
      return item.department_id || "Unknown"; // use department_id from message
    },
    (deptId) => departmentsMap[deptId]?.name || "Unknown" // label
  );
}

  // ✅ 3. DEPARTMENT → group by users
  if (selectedDepartment && selectedDepartment !== "all") {
    return buildStats(
      (item) => item.user_id,
      (id) => usersMap[id]?.name || "Unknown"
    );
  }

  // ✅ 4. DEFAULT → departments
  return buildStats(
    (item) => item.department_id,
    (id) => departmentsMap[id]?.name || "Unknown"
  );
}, [messages, usersMap, departmentsMap, selectedUser, selectedRole, selectedDepartment]);

  // Tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;
    return (
      <div
        className={cn(
          "rounded-lg border p-3 shadow-lg",
          theme === "dark"
            ? "bg-[#1a2332] border-border text-foreground"
            : "bg-card border-border text-foreground"
        )}
      >
        <p className="mb-1 text-sm font-medium">{item?.fullName || label}</p>
        <p className="text-xs">Score: <span className="font-medium">{item.risk}/5</span></p>
        <p className="text-xs">Scaled: <span className="font-medium">{item.riskPercent}%</span></p>
      </div>
    );
  };

if (!messages?.length) {
  return (
    <Card className={cn(card_bg)}>
      <CardContent className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
        {localization.no_data || "No data"}
      </CardContent>
    </Card>
  );
}

  return (
    <Card className={cn("col-span-full", card_bg)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-foreground">
         {
  selectedUser && selectedUser !== "all"
    ? "Policy Risk Over Time"
    : selectedRole && selectedRole !== "all"
    ? "Policy Risk by Department"
    : selectedDepartment && selectedDepartment !== "all"
    ? "Department Users Risk"
    : localization.policy_risk_by_department || "Policy Risk by Department"
}
        </CardTitle>
        <CardDescription className="text-xs">
          {localization.average_policy_risk_scores || "Average policy risk scores (lower is better) - scaled to percentage"}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme === "dark" ? "#1f2937" : "#e5e7eb"}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: theme === "dark" ? "#9ca3af" : "#6b7280" }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: theme === "dark" ? "#9ca3af" : "#6b7280" }}
                axisLine={false}
                tickLine={false}
                unit="%"
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
              />
              <Bar
                dataKey="riskPercent"
                name="Policy Risk"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
                barSize={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}